/**
 * Designations Edit Page — hardcoded form.
 */

import * as React from 'react';
import { useParams, useNavigate } from 'react-router';
import { useOne, useUpdate } from '@refinedev/core';
import {
  Button, Card, CardContent, Input, Label, Textarea, Checkbox,
} from '@yesbos/ui';
import { ArrowLeft } from 'lucide-react';

export function DesignationsEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading: fetchLoading } = useOne({ resource: 'designations', id: id! });
  const { mutate: update, isLoading: updateLoading } = useUpdate();
  const [form, setForm] = React.useState<Record<string, unknown> | null>(null);

  React.useEffect(() => { if (data?.data) setForm({ ...data.data }); }, [data]);
  const set = (f: string, v: unknown) => setForm(p => p ? { ...p, [f]: v } : p);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form) return;
    const { id: _id, org_id, created_at, updated_at, ...values } = form;
    update({ resource: 'designations', id: id!, values }, { onSuccess: () => navigate(`/designations/${id}`) });
  }

  if (fetchLoading || !form) return <div className="p-6 text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="p-6 max-w-xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/designations/${id}`)}><ArrowLeft className="h-4 w-4" /></Button>
        <h1 className="text-2xl font-semibold">Edit Designation</h1>
      </div>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="title">Title *</Label>
              <Input id="title" value={String(form.title ?? '')} onChange={e => set('title', e.target.value)} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="grade">Grade</Label>
                <Input id="grade" value={String(form.grade ?? '')} onChange={e => set('grade', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="level">Level</Label>
                <Input id="level" type="number" min={1} value={Number(form.level ?? 1)} onChange={e => set('level', parseInt(e.target.value) || 1)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="min_salary">Min Salary</Label>
                <Input id="min_salary" type="number" min={0} value={form.min_salary !== null && form.min_salary !== undefined ? String(form.min_salary) : ''} onChange={e => set('min_salary', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="max_salary">Max Salary</Label>
                <Input id="max_salary" type="number" min={0} value={form.max_salary !== null && form.max_salary !== undefined ? String(form.max_salary) : ''} onChange={e => set('max_salary', e.target.value)} />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" value={String(form.description ?? '')} onChange={e => set('description', e.target.value)} rows={3} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={Boolean(form.is_active)} onCheckedChange={v => set('is_active', v)} />
              <span className="text-sm">Active</span>
            </label>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate(`/designations/${id}`)}>Cancel</Button>
              <Button type="submit" disabled={updateLoading}>{updateLoading ? 'Saving...' : 'Save Changes'}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default DesignationsEditPage;
